import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ErrorBoundaryIllustration } from 'img/error_boundary_illustration.svg';

import { getOrAssignErrorId } from './reportBoundaryError';
import {
    ErrorBoundaryBody,
    ErrorBoundaryContainer,
    ErrorBoundaryDivider,
    ErrorBoundaryFooter,
    ErrorBoundaryHeading,
    ErrorBoundaryMessage,
    ErrorBoundarySupportLink,
} from './styled/ErrorBoundaryStyled';

interface ErrorBoundaryFallbackProps {
    error: unknown;
}

export const ErrorBoundaryFallback = ({ error }: ErrorBoundaryFallbackProps): ReactElement => {
    const { t } = useTranslation('global', { useSuspense: false });
    // Assigned during render so it's already set by the time the commit-phase reporting call reads it back.
    const errorId = useMemo(() => getOrAssignErrorId(error), [error]);
    const headingRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
        // No navigation occurs, so move focus here or screen readers never notice the crash.
        headingRef.current?.focus();
    }, []);

    return (
        <ErrorBoundaryContainer data-testid="error-boundary-fallback">
            <ErrorBoundaryIllustration aria-hidden="true" data-testid="error-boundary-illustration" />
            <ErrorBoundaryMessage aria-atomic="true" aria-live="assertive" role="alert">
                <ErrorBoundaryHeading ref={headingRef} tabIndex={-1}>
                    {t('error_boundary.heading')}
                </ErrorBoundaryHeading>
                <ErrorBoundaryBody>{t('error_boundary.body')}</ErrorBoundaryBody>
            </ErrorBoundaryMessage>
            <ErrorBoundaryFooter>
                <span data-testid="error-boundary-error-id">{t('error_boundary.error_id', { errorId })}</span>
                <ErrorBoundaryDivider />
                <ErrorBoundarySupportLink href={t('support_link')} target="_blank" rel="noopener noreferrer">
                    {t('contact_support')}
                </ErrorBoundarySupportLink>
            </ErrorBoundaryFooter>
        </ErrorBoundaryContainer>
    );
};
